<template>
  <v-app class="futuristic-app">
    <div class="mesh-background">
      <div class="circle c1"></div>
      <div class="circle c2"></div>
      <img src="/images/bgsub.png" class="bg-watermark-baru" />
    </div>

    <v-app-bar app flat class="ultra-glass-header mt-4 mx-6 rounded-xl">
      <v-btn icon color="black" @click="$router.back()">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <v-toolbar-title
        class="black--text font-weight-black letter-spacing-2 text-uppercase"
      >
        Peta Potensi Desa Bondowoso
      </v-toolbar-title>
    </v-app-bar>

    <v-main>
      <v-container fluid class="px-6 fill-height align-start">
        <v-row class="fill-height mt-2" no-gutters>
          <v-col cols="12" class="fill-height">
            <v-card class="cyber-map-container overflow-hidden full-height-card">
              <div class="map-corners">
                <div class="c-tl"></div>
                <div class="c-tr"></div>
                <div class="c-bl"></div>
                <div class="c-br"></div>
              </div>

              <div id="map"></div>

              <div
                class="map-legend-overlay pa-3"
                :class="{ 'is-minimized': isLegendMinimized }"
              >
                <div
                  class="d-flex align-center legend-header-row"
                  :class="{ 'mb-3': !isLegendMinimized }"
                  @click="isLegendMinimized = !isLegendMinimized"
                >
                  <div>
                    <div class="text-h6 font-weight-bold legend-title">Layer Peta</div>
                    <div v-if="!isLegendMinimized" class="text-caption grey--text">
                      Cari desa / tampilkan potensi
                    </div>
                  </div>

                  <v-spacer></v-spacer>

                  <v-btn
                    icon
                    small
                    class="minimize-btn"
                    @click.stop="isLegendMinimized = !isLegendMinimized"
                    :aria-label="
                      isLegendMinimized ? 'Buka panel layer' : 'Tutup panel layer'
                    "
                  >
                    <v-icon>{{
                      isLegendMinimized ? "mdi-chevron-down" : "mdi-chevron-up"
                    }}</v-icon>
                  </v-btn>
                </div>

                <v-expand-transition>
                  <div v-show="!isLegendMinimized">
                    <div class="village-search-wrap mb-3">
                      <v-text-field
                        v-model="villageSearchQuery"
                        dense
                        outlined
                        clearable
                        hide-details
                        prepend-inner-icon="mdi-map-search"
                        placeholder="Cari desa..."
                        class="search-field"
                        @focus="villageSearchFocus = true"
                        @input="villageSearchFocus = true"
                        @blur="onVillageSearchBlur"
                      />

                      <div
                        v-if="
                          villageSearchFocus &&
                          villageSearchQuery &&
                          filteredVillageSearch.length
                        "
                        class="village-search-dropdown"
                      >
                        <div
                          v-for="(v, i) in filteredVillageSearch"
                          :key="i"
                          class="village-search-item"
                          @mousedown.prevent="selectVillageFromSearch(v)"
                        >
                          <v-icon small color="#CFA22D" class="mr-1">
                            mdi-map-marker
                          </v-icon>
                          <span>
                            {{ v.nama }}
                            <span v-if="v.kecamatan" class="village-search-kecamatan">
                              ({{ v.kecamatan }})
                            </span>
                          </span>
                        </div>
                      </div>

                      <div
                        v-else-if="
                          villageSearchFocus &&
                          villageSearchQuery &&
                          filteredVillageSearch.length === 0
                        "
                        class="village-search-dropdown"
                      >
                        <div class="village-search-empty">Desa tidak ditemukan</div>
                      </div>
                    </div>

                    <v-divider class="mb-3"></v-divider>

                    <v-btn
                      v-for="slug in categoryOrder"
                      :key="slug"
                      block
                      rounded
                      outlined
                      class="legend-btn justify-start text-capitalize mb-2"
                      :style="
                        getButtonStyle(activeLayers[slug], categoryMeta[slug].color)
                      "
                      @click="toggleLayer(slug)"
                    >
                      <v-icon left>{{ categoryMeta[slug].icon }}</v-icon>

                      {{ categoryMeta[slug].nama }}
                    </v-btn>

                    <div
                      v-if="categoryOrder.length === 0"
                      class="text-caption grey--text text-center py-2"
                    >
                      Belum ada data potensi
                    </div>

                    <div class="text-center mt-3">
                      <v-btn x-small text color="error" @click="clearAllLayers">
                        <v-icon left x-small> mdi-filter-remove </v-icon>

                        Bersihkan Semua
                      </v-btn>
                    </div>
                  </div>
                </v-expand-transition>
              </div>

              <div
                v-if="selectedLegend"
                class="map-sidebar-overlay pa-3"
                :class="{ 'is-minimized': isSidebarMinimized }"
              >
                <div class="d-flex align-center justify-space-between mb-3">
                  <div>
                    <div class="text-h6 font-weight-bold">
                      {{ selectedLegendLabel }}
                    </div>

                    <div v-if="!isSidebarMinimized" class="text-caption grey--text">
                      Total Data:
                      {{ sidebarItems.length }}
                    </div>
                  </div>

                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      small
                      class="mr-1"
                      @click="isSidebarMinimized = !isSidebarMinimized"
                      :aria-label="
                        isSidebarMinimized ? 'Buka panel data' : 'Tutup panel data'
                      "
                    >
                      <v-icon small>{{
                        isSidebarMinimized ? "mdi-chevron-down" : "mdi-chevron-up"
                      }}</v-icon>
                    </v-btn>

                    <v-btn
                      icon
                      small
                      @click="
                        selectedLegend = null;
                        sidebarItems = [];
                      "
                    >
                      <v-icon small> mdi-close </v-icon>
                    </v-btn>
                  </div>
                </div>

                <v-expand-transition>
                  <div v-show="!isSidebarMinimized">
                    <v-divider class="mb-3"></v-divider>
                    <v-text-field
                      v-model="searchSidebar"
                      dense
                      outlined
                      clearable
                      hide-details
                      prepend-inner-icon="mdi-magnify"
                      placeholder="Cari lokasi..."
                      class="mb-3 search-field"
                    />

                    <div class="sidebar-scroll">
                      <v-card
                        v-for="(item, i) in filteredSidebarItems"
                        :key="i"
                        class="sidebar-item-card mb-3"
                        outlined
                        @click="focusToItem(item)"
                      >
                        <v-card-text>
                          <div class="d-flex align-start">
                            <div
                              class="sidebar-icon mr-3"
                              :style="{
                                background: getLayerColor(selectedLegend),
                              }"
                            >
                              <v-icon small color="white">
                                {{ getLayerIcon(selectedLegend) }}
                              </v-icon>
                            </div>

                            <div class="flex-grow-1">
                              <div class="font-weight-bold text-body-2">
                                {{ item.nama }}
                              </div>

                              <div
                                v-if="item.kategori"
                                class="text-caption grey--text mt-1"
                              >
                                {{ item.kategori }}
                              </div>

                              <div
                                class="text-caption mt-2"
                                :style="{
                                  color: getLayerColor(selectedLegend),
                                }"
                              >
                                Klik untuk melihat lokasi
                              </div>
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                      <div
                        v-if="filteredSidebarItems.length === 0"
                        class="text-center py-6 grey--text"
                      >
                        <v-icon large color="grey lighten-1"> mdi-map-search </v-icon>

                        <div class="mt-2">Data tidak ditemukan</div>
                      </div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>

              <div
                v-if="selectedVillage"
                class="map-village-overlay pa-3"
                :class="{ 'is-minimized': isVillageMinimized }"
              >
                <div class="d-flex align-center justify-space-between mb-2">
                  <div>
                    <div class="text-h6 font-weight-bold">{{ selectedVillage.nama }}</div>
                    <div v-if="!isVillageMinimized" class="text-caption grey--text">
                      Potensi Desa
                    </div>
                  </div>

                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      small
                      class="mr-1"
                      @click="isVillageMinimized = !isVillageMinimized"
                      :aria-label="
                        isVillageMinimized ? 'Buka panel desa' : 'Tutup panel desa'
                      "
                    >
                      <v-icon small>{{
                        isVillageMinimized ? "mdi-chevron-up" : "mdi-chevron-down"
                      }}</v-icon>
                    </v-btn>

                    <v-btn icon small @click="closeVillagePanel">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>

                <v-expand-transition>
                  <div v-show="!isVillageMinimized">
                    <v-divider class="mb-3"></v-divider>

                    <div
                      v-if="selectedVillage.loadingPotency"
                      class="text-center py-6 grey--text"
                    >
                      <v-progress-circular indeterminate size="24" color="#018c74" />
                      <div class="mt-2 text-caption">Memuat data potensi...</div>
                    </div>

                    <div v-else class="village-potency-row">
                      <div
                        v-for="type in categoryOrder"
                        :key="type"
                        class="village-potency-col"
                      >
                        <div class="d-flex align-center mb-1">
                          <v-icon small :color="getLayerColor(type)" class="mr-1">
                            {{ getLayerIcon(type) }}
                          </v-icon>

                          <span class="text-caption font-weight-bold text-capitalize">{{
                            getLayerLabel(type)
                          }}</span>

                          <v-chip
                            x-small
                            class="ml-2"
                            :color="getLayerColor(type)"
                            text-color="white"
                          >
                            {{ (villagePotency[type] || []).length }}
                          </v-chip>
                        </div>

                        <div
                          v-if="
                            !villagePotency[type] || villagePotency[type].length === 0
                          "
                          class="text-caption grey--text"
                        >
                          Tidak ada data
                        </div>

                        <div v-else class="village-potency-list">
                          <div
                            v-for="(item, idx) in villagePotency[type]"
                            :key="idx"
                            class="text-caption village-potency-item d-flex align-center justify-space-between"
                          >
                            <span>
                              • {{ item.nama }}
                              <span v-if="item.kategori" class="grey--text">
                                ({{ item.kategori }})</span
                              >
                            </span>

                            <v-btn
                              x-small
                              text
                              color="#018c74"
                              class="detail-btn"
                              @click="openDetail(item)"
                            >
                              Detail
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <v-dialog
      v-model="detailDialog"
      max-width="620"
      width="95%"
      transition="dialog-bottom-transition"
    >
      <v-card v-if="detailItem" class="detail-dialog-card">

        <div class="hero-media">
          <v-btn
            fab
            x-small
            elevation="3"
            color="white"
            class="close-btn"
            @click="closeDetail"
          >
            <v-icon color="#333"> mdi-close </v-icon>
          </v-btn>

          <template v-if="detailMedia.length">
            <v-carousel
              v-model="detailCarouselIndex"
              height="240"
              hide-delimiter-background
              :show-arrows="detailMedia.length > 1"
              :hide-delimiters="detailMedia.length <= 1"
              class="hero-carousel"
            >
              <v-carousel-item v-for="(media, i) in detailMedia" :key="i">
                <img v-if="media.type == 'image'" :src="media.url" class="hero-img" />

                <video
                  v-else
                  controls
                  preload="metadata"
                  playsinline
                  class="hero-video"
                  :src="media.url"
                ></video>

                <div class="media-type">
                  <v-icon x-small color="white">
                    {{ media.type == "video" ? "mdi-play-circle" : "mdi-image" }}
                  </v-icon>

                  <span>
                    {{ media.type == "video" ? "Video" : "Foto" }}
                  </span>
                </div>

                <div class="media-count">{{ i + 1 }} / {{ detailMedia.length }}</div>
              </v-carousel-item>
            </v-carousel>
          </template>

          <template v-else>
            <div class="no-media">
              <v-icon size="70" color="grey lighten-1"> mdi-image-off-outline </v-icon>

              <div class="mt-3 grey--text">Belum ada foto / video</div>
            </div>
          </template>
        </div>


        <div class="pa-5">
          <div class="detail-title">
            {{ detailItem.nama }}
          </div>

          <div class="mt-2">
            <v-chip x-small color="#018c74" dark>
              {{ detailItem.nama_kategori }}
            </v-chip>

            <v-chip x-small outlined class="ml-1" v-if="detailItem.nama_jenis">
              {{ detailItem.nama_jenis }}
            </v-chip>
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="info-row" v-if="detailItem.alamat">
            <v-icon small color="#018c74"> mdi-map-marker </v-icon>

            <span>
              {{ detailItem.alamat }}
            </span>
          </div>

          <div class="info-row" v-if="detailItem.nama_desa_raw">
            <v-icon small color="#018c74"> mdi-map-marker </v-icon>
            <span>
              Desa {{ detailItem.nama_desa_raw
              }}<template v-if="detailItem.kecamatan"
                >, {{ detailItem.kecamatan }}</template
              >
            </span>
          </div>

          <div class="info-row" v-if="detailItem.pic">
            <v-icon small color="#018c74"> mdi-account-circle </v-icon>

            <span>
              {{ detailItem.pic }}
            </span>
          </div>

          <div class="description" v-if="detailItem.deskripsi">
            {{ detailItem.deskripsi }}
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import * as turf from "@turf/turf";
import { getPotensiSpasial } from "@/api/androidInitApi";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const MEDIA_BASE_URL = "https://berkah.bondowosokab.go.id:5500/uploads/";

const CATEGORY_COLOR_PALETTE = [
  "#FF9800",
  "#4CAF50",
  "#2196F3",
  "#9C27B0",
  "#E91E63",
  "#00BCD4",
  "#795548",
  "#607D8B",
  "#CDDC39",
  "#3F51B5",
];

export default {
  data() {
    return {
      map: null,

      markerClusters: {},

      searchSidebar: "",
      selectedLegend: null,
      sidebarItems: [],

      masterData: null,

      geojsonData: {},

      categoryMeta: {},
      categoryOrder: [],

      boundaryLayer: null,
      districtLayer: null,

      districtIndexById: {},
      districtFeatures: [],

      villageLayer: null,
      selectedVillageLayer: null,
      selectedVillage: null,
      villagePotency: {},

      villageSearchQuery: "",
      villageSearchFocus: false,
      villageList: [],

      layers: {},

      activeLayers: {},

      isLegendMinimized: false,
      isSidebarMinimized: false,
      isVillageMinimized: false,

      detailDialog: false,
      detailItem: null,
      detailVideo: null,
      detailPhotos: [],
      detailCarouselIndex: 0,

      villageList: [],
      villageDistrictMap: {},
    };
  },

  async mounted() {
    this.isLegendMinimized = window.innerWidth <= 768;

    this.initMap();

    await this.loadMasterData();

    await this.loadBoundary();
    await this.loadDistrict();
    await this.loadVillage();
  },

  computed: {
    detailMedia() {
      const media = [];

      if (this.detailVideo) {
        media.push({
          type: "video",
          url: this.detailVideo.url,
        });
      }

      this.detailPhotos.forEach((item) => {
        media.push({
          type: "image",
          url: item.url,
        });
      });

      return media;
    },

    selectedLegendLabel() {
      if (!this.selectedLegend) return "";
      const meta = this.categoryMeta[this.selectedLegend];
      return meta ? `Data ${meta.nama}` : "";
    },

    filteredSidebarItems() {
      if (!this.searchSidebar) {
        return this.sidebarItems;
      }

      return this.sidebarItems.filter((item) =>
        item.nama.toLowerCase().includes(this.searchSidebar.toLowerCase())
      );
    },

    filteredVillageSearch() {
      if (!this.villageSearchQuery) return [];

      const q = this.villageSearchQuery.toLowerCase().trim();

      const startsWith = [];
      const contains = [];

      for (const v of this.villageList) {
        const nama = (v.nama || "").toLowerCase();
        const kec = (v.kecamatan || "").toLowerCase();
        const searchKey = v.searchKey || `${nama} ${kec}`;

        const match = nama.includes(q) || kec.includes(q) || searchKey.includes(q);

        if (!match) continue;

        if (nama.startsWith(q)) {
          startsWith.push(v);
        } else {
          contains.push(v);
        }
      }

      return [...startsWith, ...contains].slice(0, 12);
    },
  },

  methods: {
    determineMediaType(filename) {
      const ext = String(filename).split(".").pop().toLowerCase();
      const videoExt = ["mp4", "mov", "avi", "mkv", "webm"];
      return videoExt.includes(ext) ? "video" : "foto";
    },

    parseFotoField(fotoRaw) {
      if (!fotoRaw) return [];

      let arr = fotoRaw;
      if (typeof fotoRaw === "string") {
        try {
          arr = JSON.parse(fotoRaw);
        } catch (e) {
          return [];
        }
      }

      if (!Array.isArray(arr)) return [];

      return arr.map((filename) => ({
        filename,
        url: `${MEDIA_BASE_URL}${filename}`,
        type: this.determineMediaType(filename),
      }));
    },

    openDetail(item) {
      const p = item.raw || {};

      this.detailItem = {
        nama: item.nama,
        kategori: item.kategori,
        nama_kategori: p.nama_kategori || "",
        nama_jenis: p.nama_jenis || "",
        deskripsi: p.deskripsi || "",
        nama_desa_raw: p.nama_desa_raw || "",
        kecamatan: this.resolveKecamatanForProps(p),
        alamat: p.alamat || "",
        pic: p.pic || "",
      };

      const media = this.parseFotoField(p.foto);

      this.detailVideo = media.find((m) => m.type === "video") || null;
      this.detailPhotos = media.filter((m) => m.type === "foto");
      this.detailCarouselIndex = 0;

      this.detailDialog = true;
    },

    closeDetail() {
      this.detailDialog = false;
      this.detailItem = null;
      this.detailVideo = null;
      this.detailPhotos = [];
    },

    normalizeText(val) {
      return String(val || "")
        .trim()
        .replace(/\s+/g, " ");
    },

    normalizeDistrictId(val) {
      if (val === null || val === undefined || val === "") return "";
      return String(parseInt(String(val), 10));
    },

    slugifyKategori(nama) {
      const slug = String(nama || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

      return slug || "lainnya";
    },

    pickCategoryColor(index) {
      return CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length];
    },

    getCategoryIcon(namaKategori) {
      const k = String(namaKategori || "").toLowerCase();

      if (k.includes("wisata")) return "mdi-map-marker-star";
      if (k.includes("ternak")) return "mdi-cow";
      if (k.includes("ikan")) return "mdi-fish";

      return "mdi-map-marker-radius";
    },

    buildDistrictIndex(geojson) {
      this.districtIndexById = {};
      this.districtFeatures = geojson?.features || [];

      (geojson?.features || []).forEach((feature) => {
        const p = feature.properties || {};
        const nama = this.normalizeText(p.nm_kecamatan || p.NAMOBJ || p.WADMKC || "");

        if (!nama) return;

        const rawCandidates = [
          p.kecamatan_id,
          p.kd_kecamatan,
          p.KD_KECAMATAN,
          p.kode_kecamatan,
          p.id,
          p.OBJECTID,
        ].filter((v) => v !== null && v !== undefined && v !== "");

        rawCandidates.forEach((raw) => {
          const rawKey = String(raw).trim();
          if (rawKey) this.districtIndexById[rawKey] = nama;

          const normalized = this.normalizeDistrictId(raw);
          if (normalized) this.districtIndexById[normalized] = nama;
        });
      });
    },

    async loadMasterData() {
      try {
        const potensiSpasial = (await getPotensiSpasial()) || [];

        this.masterData = { potensi_spasial: potensiSpasial };

        this.buildGeojsonFromPotensiSpasial(
          Array.isArray(potensiSpasial) ? potensiSpasial : []
        );
      } catch (e) {
        console.error("API Error", e);
      }
    },

    buildGeojsonFromPotensiSpasial(potensiSpasial) {
      const grouped = {};
      const meta = {};
      const order = [];

      potensiSpasial.forEach((item) => {
        const namaKategori = item.nama_kategori || "Lainnya";
        const slug = this.slugifyKategori(namaKategori);

        if (!grouped[slug]) {
          grouped[slug] = [];
          order.push(slug);

          meta[slug] = {
            id: item.kategori_id,
            nama: namaKategori,
            color: this.pickCategoryColor(order.length - 1),
            icon: this.getCategoryIcon(namaKategori),
          };
        }

        const lng = Number(item.longitude);
        const lat = Number(item.latitude);

        if (!isFinite(lng) || !isFinite(lat)) return;

        grouped[slug].push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
          properties: {
            id: item.id,
            nama: item.toponim || item.nama_sub_jenis || "-",
            kategori: item.nama_sub_jenis || "",
            nama_desa_raw: item.nama_desa_raw || "",
            nama_kecamatan: item.nama_kecamatan || "",
            kategori_id: item.kategori_id,
            sub_jenis_id: item.sub_jenis_id,
            nama_kategori: namaKategori,
            nama_jenis: item.nama_jenis || "",
            deskripsi: item.deskripsi || "",
            alamat: item.alamat || "",
            pic: item.pic || "",
            foto: item.foto || null,
          },
        });
      });

      this.categoryMeta = meta;
      this.categoryOrder = order;

      const newGeojsonData = {};
      const newVillagePotency = {};

      order.forEach((slug) => {
        newGeojsonData[slug] = {
          type: "FeatureCollection",
          features: grouped[slug],
        };
        newVillagePotency[slug] = [];
      });

      this.geojsonData = newGeojsonData;
      this.villagePotency = newVillagePotency;
    },

    resolveKecamatanForProps(p) {
      const direct = this.normalizeText(p.nama_kecamatan);
      if (direct) return direct;

      const key = this.normalizeText(p.nama_desa_raw).toLowerCase();
      return this.villageDistrictMap[key] || "";
    },

    initMap() {
      this.map = L.map("map", {
        zoomControl: false,
      }).setView([-7.909473813658169, 113.8981324393563], 12);

      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
  attribution: "&copy; OpenStreetMap contributors",
}).addTo(this.map);

      L.control
        .zoom({
          position: "bottomright",
        })
        .addTo(this.map);

      this.$nextTick(() => {
        setTimeout(() => {
          this.map.invalidateSize();
        }, 300);
      });
    },

    async toggleLayer(type) {
      this.closeVillagePanel();

      if (this.selectedLegend === type) {
        if (this.markerClusters[type]) {
          this.map.removeLayer(this.markerClusters[type]);
        }

        this.$set(this.activeLayers, type, false);

        this.selectedLegend = null;

        this.sidebarItems = [];

        return;
      }

      Object.keys(this.layers).forEach((key) => {
        if (this.layers[key]) {
          this.map.removeLayer(this.layers[key]);
        }

        this.$set(this.activeLayers, key, false);
      });

      this.selectedLegend = type;
      this.isSidebarMinimized = false;

      if (!this.layers[type]) {
        try {
          const geojson = await this.loadPotencyData(type);

          if (!geojson || !geojson.features?.length) {
            this.$set(this.layers, type, null);
          } else {
            const builtLayer = L.geoJSON(geojson, {
              filter: (feature) => {
                const coords = feature.geometry?.coordinates;

                if (!coords || coords.length < 2) return false;

                const lng = coords[0];
                const lat = coords[1];

                return (
                  isFinite(lat) &&
                  isFinite(lng) &&
                  Math.abs(lat) <= 90 &&
                  Math.abs(lng) <= 180
                );
              },

              pointToLayer: (feature, latlng) => {
                return L.circleMarker(latlng, {
                  radius: 8,
                  fillColor: this.getLayerColor(type),
                  color: "#ffffff",
                  weight: 2,
                  fillOpacity: 0.9,
                });
              },

              onEachFeature: (feature, layer) => {
                const p = feature.properties || {};
                const nama = p.nama || p.REMARK || p.Jenis_Pete || "-";
                const kecamatan = this.resolveKecamatanForProps(p);

                layer.bindTooltip(nama, {
                  permanent: true,
                  direction: "top",
                  offset: [0, -10],
                  className: "marker-label",
                });

                layer.bindPopup(`
    <div style="min-width:220px">
      <div style="font-size:16px;font-weight:700;margin-bottom:8px;">
        ${nama}
      </div>
      ${p.nama_kategori ? `<div><b>Kategori:</b> ${p.nama_kategori}</div>` : ""}
      ${p.kategori ? `<div><b>Sub Jenis:</b> ${p.kategori}</div>` : ""}
      ${
        p.nama_desa_raw
          ? `<div><b>Desa:</b> ${p.nama_desa_raw}${
              kecamatan ? ", Kec. " + kecamatan : ""
            }</div>`
          : ""
      }
    </div>
  `);
              },
            });

            this.$set(this.layers, type, builtLayer);
          }
        } catch (e) {
          console.error(`Gagal load ${type}`, e);
          return;
        }
      }

      if (!this.layers[type]) {
        this.$set(this.activeLayers, type, true);
        this.sidebarItems = [];
        return;
      }

      if (this.markerClusters[type]) {
        this.map.removeLayer(this.markerClusters[type]);
      }

      const cluster = L.markerClusterGroup({
        chunkedLoading: true,

        spiderfyOnMaxZoom: true,

        showCoverageOnHover: false,

        zoomToBoundsOnClick: true,

        disableClusteringAtZoom: 15,
      });

      cluster.addLayer(this.layers[type]);

      this.map.addLayer(cluster);

      this.$set(this.markerClusters, type, cluster);

      this.$set(this.activeLayers, type, true);

      this.sidebarItems = (this.geojsonData[type]?.features || [])
        .filter((f) => {
          const coords = f.geometry?.coordinates;

          if (!coords) return false;

          const lng = coords[0];
          const lat = coords[1];

          return (
            isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
          );
        })
        .map((f) => ({
          nama:
            f.properties.nama || f.properties.REMARK || f.properties.Jenis_Pete || "-",

          kategori: f.properties.kategori || f.properties.KATEGORI || "",

          coordinates: f.geometry.coordinates,

          properties: f.properties,
        }));

      const bounds = this.layers[type].getBounds();

      if (bounds.isValid()) {
        this.map.flyToBounds(bounds, {
          padding: [50, 50],
          duration: 1,
        });
      }
    },

    resolveVillageDistrictName(feature) {
      const p = feature?.properties || {};

      const directName =
        p.nm_kecamatan || p.NAMOBJ_1 || p.WADMKC || p.kecamatan || p.nama_kecamatan || "";

      if (this.normalizeText(directName)) {
        return this.normalizeText(directName);
      }

      const rawDistrictId =
        p.kecamatan_id ||
        p.kd_kecamatan ||
        p.KD_KECAMATAN ||
        p.kode_kecamatan ||
        p.id_kecamatan ||
        "";

      const rawKey = String(rawDistrictId || "").trim();
      if (rawKey && this.districtIndexById[rawKey]) {
        return this.districtIndexById[rawKey];
      }

      const normalizedKey = this.normalizeDistrictId(rawDistrictId);
      if (normalizedKey && this.districtIndexById[normalizedKey]) {
        return this.districtIndexById[normalizedKey];
      }

      return "";
    },

    clearAllLayers() {
      Object.keys(this.layers).forEach((key) => {
        if (this.layers[key]) {
          this.map.removeLayer(this.layers[key]);
        }

        if (this.markerClusters[key]) {
          this.map.removeLayer(this.markerClusters[key]);
        }

        this.$set(this.activeLayers, key, false);
      });

      this.closeVillagePanel();
    },

    getLayerColor(type) {
      return (this.categoryMeta[type] && this.categoryMeta[type].color) || "#999";
    },

    getLayerIcon(type) {
      return (
        (this.categoryMeta[type] && this.categoryMeta[type].icon) ||
        "mdi-map-marker-radius"
      );
    },

    getLayerLabel(type) {
      return (this.categoryMeta[type] && this.categoryMeta[type].nama) || type;
    },

    getButtonStyle(active, color) {
      return {
        borderColor: color,
        background: active ? color : "rgba(255,255,255,0.95)",
        color: active ? "white" : color,
      };
    },

    async loadDistrict() {
      try {
        const response = await fetch("/images/35.11_kecamatan.geojson");
        const geojson = await response.json();

        this.buildDistrictIndex(geojson);

        const districtColorMap = {
          maesan: "#432219",
          tamanan: "#928773",
          tlogosari: "#acb5b4",
          sukosari: "#3f2822",
          pujer: "#9c9680",
          grujugan: "#12232a",
          curahdami: "#978b73",
          tenggarang: "#94856d",
          wonosari: "#b65221",
          tapen: "#593125",
          bondowoso: "#b1bbba",
          wringin: "#4e281e",
          tegalampel: "#452a23",
          klabang: "#a7b1b0",
          cermee: "#3b2620",
          prajekan: "#9f9177",
          pakem: "#b65221",
          sumberwringin: "#918972",
          ijen: "#8A9A5B",
          binakal: "#9e9784",
          "taman krocok": "#14252c",
          botolinggo: "#1a3139",
          "jambesari ds": "#35211a",
        };

        const fallbackColors = Object.values(districtColorMap);

        this.districtLayer = L.geoJSON(geojson, {
          style: (feature) => {
            const nama =
              feature.properties?.nm_kecamatan ||
              feature.properties?.NAMOBJ ||
              feature.properties?.WADMKC ||
              "";
            const key = nama.toString().trim().toLowerCase();
            const fillColor =
              districtColorMap[key] ||
              fallbackColors[
                (feature.properties?.OBJECTID ||
                  Math.floor(Math.random() * fallbackColors.length)) %
                  fallbackColors.length
              ];

            return {
              color: "#ffffff",
              weight: 2,
              opacity: 1,
              fillColor,
              fillOpacity: 0.55,
            };
          },
          onEachFeature: (feature, layer) => {
            const nama =
              feature.properties.nm_kecamatan ||
              feature.properties.NAMOBJ ||
              feature.properties.WADMKC ||
              "Kecamatan";

            layer.bindTooltip(nama, {
              permanent: true,
              direction: "center",
              className: "label-kecamatan",
            });

            layer.bindPopup(`
          <div style="min-width:180px">
            <div style="
              font-size:16px;
              font-weight:700;
              margin-bottom:8px;
            ">
              ${nama}
            </div>
            <div>
              Wilayah Kecamatan ${nama}
            </div>
          </div>
        `);

            layer.on({
              mouseover: (e) => {
                e.target.setStyle({
                  weight: 3,
                  fillOpacity: 0.75,
                });
              },
              mouseout: (e) => {
                this.districtLayer.resetStyle(e.target);
              },
            });
          },
        }).addTo(this.map);
      } catch (e) {
        console.error("Gagal load batas kecamatan", e);
      }
    },

    async loadBoundary() {
      try {
        const response = await fetch("/images/35.11_Bondowoso.geojson");

        const geojson = await response.json();

        this.boundaryLayer = L.geoJSON(geojson, {
          style: {
            color: "#018c74",
            weight: 4,
            fillOpacity: 0,
          },
        }).addTo(this.map);

      } catch (e) {
        console.error("Gagal load batas Bondowoso", e);
      }
    },

    async loadPotencyData(type) {
      if (this.geojsonData[type]) {
        return this.geojsonData[type];
      }

      await this.loadMasterData();

      return this.geojsonData[type] || { type: "FeatureCollection", features: [] };
    },

    async loadVillage() {
      try {
        const response = await fetch("/images/35.11_desa.geojson");
        const geojson = await response.json();

        this.villageLayer = L.geoJSON(geojson, {
          style: {
            color: "#ffffff",
            weight: 1,
            opacity: 0.75,
            fillColor: "#ffffff",
            fillOpacity: 0,
          },

          onEachFeature: (feature, layer) => {
            const nama =
              feature.properties?.nama_desa ||
              feature.properties?.NAMOBJ ||
              feature.properties?.WADMKD ||
              "Desa";

            const kecamatan = this.resolveVillageDistrictName(feature);

            if (kecamatan && !this.villageDistrictMap[nama.toLowerCase()]) {
              this.villageDistrictMap[nama.toLowerCase()] = kecamatan;
            }

            this.villageList.push({
              nama,
              kecamatan,
              searchKey: `${nama} ${kecamatan}`.toLowerCase(),
              feature,
              layer,
            });

            layer.bindTooltip(nama, {
              permanent: true,
              direction: "center",
              className: "label-desa",
            });

            layer.on({
              mouseover: (e) => {
                if (e.target === this.selectedVillageLayer) return;
                e.target.setStyle({
                  weight: 2,
                  fillOpacity: 0.15,
                });
              },

              mouseout: (e) => {
                if (e.target === this.selectedVillageLayer) return;
                this.villageLayer.resetStyle(e.target);
              },

              click: (e) => {
                L.DomEvent.stopPropagation(e);
                this.selectVillage(feature, layer, nama);
              },
            });
          },
        }).addTo(this.map);
      } catch (e) {
        console.error("Gagal load batas desa", e);
      }
    },

    async selectVillage(feature, layer, nama) {
      this.selectedLegend = null;
      this.sidebarItems = [];

      if (this.selectedVillageLayer) {
        this.villageLayer.resetStyle(this.selectedVillageLayer);
      }
      this.selectedVillageLayer = layer;
      layer.setStyle({
        color: "#CFA22D",
        weight: 3,
        opacity: 1,
        fillColor: "#CFA22D",
        fillOpacity: 0.25,
      });
      if (layer.bringToFront) layer.bringToFront();

      this.isVillageMinimized = false;

      this.selectedVillage = {
        nama,
        kecamatan: this.resolveVillageDistrictName(feature),
        properties: feature.properties,
        loadingPotency: true,
      };

      const resetPotency = {};
      this.categoryOrder.forEach((slug) => {
        resetPotency[slug] = [];
      });
      this.villagePotency = resetPotency;

      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        this.map.flyToBounds(bounds, {
          padding: [60, 60],
          duration: 1.2,
        });
      }

      const potency = await this.computeVillagePotency(feature);
      this.villagePotency = potency;
      if (this.selectedVillage) {
        this.selectedVillage.loadingPotency = false;
      }
    },

    async computeVillagePotency(villageFeature) {
      const result = {};

      for (const type of this.categoryOrder) {
        const data = await this.loadPotencyData(type);

        if (!data || !data.features) {
          result[type] = [];
          continue;
        }

        result[type] = data.features
          .filter((f) => {
            const coords = f.geometry?.coordinates;
            if (!coords || coords.length < 2) return false;

            const [lng, lat] = coords;
            if (
              !isFinite(lat) ||
              !isFinite(lng) ||
              Math.abs(lat) > 90 ||
              Math.abs(lng) > 180
            ) {
              return false;
            }

            try {
              const pt = turf.point([lng, lat]);
              return turf.booleanPointInPolygon(pt, villageFeature);
            } catch (e) {
              return false;
            }
          })
          .map((f) => ({
            nama:
              f.properties?.nama ||
              f.properties?.REMARK ||
              f.properties?.Jenis_Pete ||
              "-",
            kategori: f.properties?.kategori || f.properties?.KATEGORI || "",
            raw: f.properties || {},
          }));
      }

      return result;
    },

    closeVillagePanel() {
      if (this.selectedVillageLayer && this.villageLayer) {
        this.villageLayer.resetStyle(this.selectedVillageLayer);
      }
      this.selectedVillageLayer = null;
      this.selectedVillage = null;

      const resetPotency = {};
      this.categoryOrder.forEach((slug) => {
        resetPotency[slug] = [];
      });
      this.villagePotency = resetPotency;
    },

    onVillageSearchBlur() {
      setTimeout(() => {
        this.villageSearchFocus = false;
      }, 150);
    },

    selectVillageFromSearch(item) {
      this.villageSearchQuery = item.nama;
      this.villageSearchFocus = false;
      this.selectVillage(item.feature, item.layer, item.nama);
    },

    focusToItem(item) {
      const lng = item.coordinates[0];
      const lat = item.coordinates[1];

      this.map.flyTo([lat, lng], 15, {
        duration: 1.5,
      });

      const popup = `
    <div style="min-width:220px">
      <div style="
        font-size:16px;
        font-weight:700;
        margin-bottom:8px;
      ">
        ${item.nama}
      </div>

      ${
        item.kategori
          ? `
            <div>
              <b>Kategori:</b>
              ${item.kategori}
            </div>
          `
          : ""
      }
    </div>
  `;

      L.popup().setLatLng([lat, lng]).setContent(popup).openOn(this.map);
    },
  },
};
</script>

<style scoped>
.detail-dialog-card {
  border-radius: 22px;

  overflow: hidden;
}

.hero-media {
  position: relative;
}

.hero-carousel {
  background: #eceff1;
}

.hero-img,
.hero-video {
  width: 100%;
  height: 100%;

  object-fit: cover;
}

.close-btn {
  position: absolute;

  right: 12px;

  top: 12px;

  z-index: 9999 !important;

  width: 38px;

  height: 38px;

  background: rgba(255, 255, 255, 0.92) !important;

  backdrop-filter: blur(10px);

  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
}

.media-type {
  position: absolute;

  left: 12px;

  top: 12px;

  display: flex;

  align-items: center;

  gap: 5px;

  padding: 5px 10px;

  font-size: 11px;

  color: white;

  background: rgba(0, 0, 0, 0.45);

  border-radius: 18px;

  backdrop-filter: blur(8px);
}

.media-count {
  position: absolute;

  right: 12px;

  bottom: 12px;

  padding: 5px 10px;

  font-size: 11px;

  color: white;

  background: rgba(0, 0, 0, 0.45);

  border-radius: 18px;
}

.no-media {
  height: 220px;

  display: flex;

  flex-direction: column;

  justify-content: center;

  align-items: center;

  background: #f5f5f5;
}

.detail-title {
  font-size: 22px;

  font-weight: 700;

  color: #222;

  line-height: 1.3;
}

.info-row {
  display: flex;

  align-items: flex-start;

  gap: 10px;

  margin-bottom: 12px;

  font-size: 13px;

  color: #555;
}

.description {
  margin-top: 18px;

  font-size: 13px;

  line-height: 1.8;

  color: #666;
}

:root {
  --accent-gold: #cfa22d;
  --accent-teal: #018c74;
}

.full-height-card {
  height: calc(100vh - 140px);
  min-height: 650px;
}

.cyber-map-container {
  position: relative;
  border-radius: 24px !important;
  overflow: hidden;
  border: 1px solid rgba(1, 140, 116, 0.35) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2) !important;
}

#map {
  width: 100%;
  height: 100%;
  z-index: 1;
}

.mesh-background {
  position: fixed;
  inset: 0;
  background: #f2f7fb;
  z-index: 0;
}

.circle {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.22;
}

.bg-watermark-baru {
  width: 100%;
  height: 100%;
}

.c1 {
  width: 600px;
  height: 600px;
  background: linear-gradient(90deg, #016b62 0%, #018c74 45%, #8bcdc6 100%);
  top: -10%;
  left: -10%;
}

.c2 {
  width: 500px;
  height: 500px;
  background: #cfa22d;
  bottom: -5%;
  right: -5%;
}

.ultra-glass-header {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(1, 140, 116, 0.25) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.map-legend-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 100;

  width: 290px;

  background: rgba(255, 255, 255, 0.95);

  backdrop-filter: blur(12px);

  border-radius: 18px;

  border: 1px solid rgba(1, 140, 116, 0.3);

  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);

  transition: width 0.25s ease, padding 0.25s ease;
}

.map-legend-overlay.is-minimized,
.map-sidebar-overlay.is-minimized,
.map-village-overlay.is-minimized {
  width: auto;
  max-height: none;
  padding: 6px 10px !important;
  border-radius: 999px;
}

.legend-header-row {
  cursor: pointer;
}

.legend-title {
  white-space: nowrap;
}

.map-legend-overlay.is-minimized .legend-title {
  font-size: 11px;
}

.minimize-btn {
  flex-shrink: 0;
}

.legend-btn {
  transition: all 0.2s ease;
  font-size: 13px !important;
  height: 42px !important;
}

.village-search-wrap {
  position: relative;
}

.village-search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1001;

  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);

  max-height: 220px;
  overflow-y: auto;
}

.village-search-item {
  display: flex;
  align-items: center;

  padding: 8px 10px;
  font-size: 13px;
  cursor: pointer;

  transition: background 0.15s ease;
}

.village-search-item:hover {
  background: rgba(1, 140, 116, 0.08);
}

.village-search-kecamatan {
  color: #888;
  font-size: 11.5px;
  font-weight: 500;
}

.village-search-empty {
  padding: 10px;
  font-size: 12px;
  color: #888;
  text-align: center;
}

.map-corners div {
  position: absolute;
  width: 30px;
  height: 30px;
  border: 3px solid var(--accent-gold);
  z-index: 10;
  pointer-events: none;
}

.c-tl {
  top: 15px;
  left: 15px;
  border-right: 0;
  border-bottom: 0;
}

.c-tr {
  top: 15px;
  right: 15px;
  border-left: 0;
  border-bottom: 0;
}

.c-bl {
  bottom: 15px;
  left: 15px;
  border-right: 0;
  border-top: 0;
}

.c-br {
  bottom: 15px;
  right: 15px;
  border-left: 0;
  border-top: 0;
}

@media (max-width: 768px) {
  .ultra-glass-header {
    margin: 8px 8px 0 !important;
  }

  .ultra-glass-header .v-toolbar-title {
    font-size: 0.85rem !important;
    letter-spacing: 0.5px !important;
    white-space: normal !important;
    line-height: 1.2;
  }

  .full-height-card {
    height: calc(100vh - 110px);
    min-height: 500px;
  }

  .map-legend-overlay {
    width: calc(100% - 24px);
    max-width: none;
    top: 12px;
    left: 12px;
    right: 12px;
    padding: 10px !important;
  }

  .map-legend-overlay.is-minimized {
    width: auto;
    right: auto;
  }

  .legend-btn {
    height: 38px !important;
    font-size: 12px !important;
  }

  .map-sidebar-overlay {
    width: calc(100% - 24px);
    max-width: none;
    top: auto;
    right: 12px;
    left: 12px;
    bottom: 12px;
    max-height: 50vh;
  }

  .map-sidebar-overlay.is-minimized {
    width: auto;
    left: auto;
    max-height: none;
  }

  .sidebar-scroll {
    max-height: calc(50vh - 140px);
  }

  .map-village-overlay {
    width: calc(100% - 24px);
    max-width: none;
    left: 12px;
    right: 12px;
    bottom: 12px;
    transform: none;
    max-height: 50vh;
  }

  .map-village-overlay.is-minimized {
    width: auto;
    left: auto;
    right: 12px;
    max-height: none;
  }

  .village-potency-row {
    flex-direction: column;
    gap: 12px;
  }

  .village-potency-list {
    max-height: 110px;
  }

  .map-corners div {
    width: 20px;
    height: 20px;
    border-width: 2px;
  }

  .c-tl,
  .c-tr {
    top: 8px;
  }

  .c-bl,
  .c-br {
    bottom: 8px;
  }

  .c-tl,
  .c-bl {
    left: 8px;
  }

  .c-tr,
  .c-br {
    right: 8px;
  }
}

@media (max-width: 480px) {
  .map-legend-overlay .text-overline {
    font-size: 0.65rem !important;
  }

  .sidebar-item-card .text-body-2 {
    font-size: 0.8rem !important;
  }

  .sidebar-icon {
    width: 34px;
    height: 34px;
  }
}

::v-deep .label-kecamatan {
  background: rgba(255, 255, 255, 0.7) !important;
  border: none !important;
  box-shadow: none !important;

  color: #111 !important;

  font-size: 11px !important;
  font-weight: 800 !important;

  padding: 2px 6px !important;

  border-radius: 8px;

  text-transform: uppercase;

  backdrop-filter: blur(4px);
}

::v-deep .label-desa {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;

  color: #ffffff !important;
  font-size: 9px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  opacity: 1 !important;
  white-space: nowrap !important;

  text-shadow: -1px -1px 0 rgba(0, 0, 0, 0.85), 1px -1px 0 rgba(0, 0, 0, 0.85),
    -1px 1px 0 rgba(0, 0, 0, 0.85), 1px 1px 0 rgba(0, 0, 0, 0.85),
    0px 0px 3px rgba(0, 0, 0, 0.7);
}

::v-deep .marker-label {
  background: rgba(255, 255, 255, 0.95) !important;

  border: none !important;

  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2) !important;

  color: #111 !important;

  font-size: 11px !important;
  font-weight: 700 !important;

  padding: 4px 8px !important;

  border-radius: 10px;

  backdrop-filter: blur(4px);
}

.map-sidebar-overlay {
  position: absolute;

  top: 20px;
  right: 20px;

  width: 360px;

  max-height: calc(100vh - 180px);

  z-index: 101;

  background: rgba(255, 255, 255, 0.96);

  backdrop-filter: blur(14px);

  border-radius: 20px;

  border: 1px solid rgba(1, 140, 116, 0.2);

  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);

  overflow: hidden;

  transition: width 0.25s ease, padding 0.25s ease;
}

.sidebar-scroll {
  overflow-y: auto;

  max-height: calc(100vh - 280px);

  padding-right: 4px;
}

.sidebar-item-card {
  cursor: pointer;

  border-radius: 16px !important;

  transition: all 0.25s ease;

  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.sidebar-item-card:hover {
  transform: translateY(-3px);

  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12) !important;
}

.sidebar-icon {
  width: 42px;
  height: 42px;

  border-radius: 14px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;
}

.search-field ::v-deep .v-input__slot {
  border-radius: 14px !important;

  background: rgba(255, 255, 255, 0.9);
}

.map-village-overlay {
  position: absolute;

  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);

  width: min(94%, 820px);
  max-height: 260px;

  z-index: 102;

  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(14px);

  border-radius: 18px;
  border: 1px solid rgba(207, 162, 45, 0.35);

  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);

  overflow: hidden;
  overflow-y: auto;

  transition: width 0.25s ease, padding 0.25s ease;
}

.village-potency-row {
  display: flex;
  gap: 20px;
}

.village-potency-col {
  flex: 1;
  min-width: 0;
}

.village-potency-list {
  max-height: 140px;
  overflow-y: auto;
  padding-right: 4px;
}

.village-potency-item {
  padding: 2px 0;
}
</style>
